// composables/usePageSeo.ts
import { computed, type MaybeRef, unref } from "vue";
import {
  useHead,
  useLocalePath,
  useRequestURL,
  useRoute,
  useSeoMeta,
} from "#imports";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { localeOpenGraphTag } from "~/shared/locales";

export interface SeoInfo {
  name: string;
  short_description?: string | null;
  photo: string | null;
  icon: string | null;
  schemaType: "product" | "website" | "article" | string;
  canonicalPath?: string;
  indexable?: boolean;
  publishedAt?: string;
  modifiedAt?: string;

  sku?: string;
  brand?: string;
  brandUrl?: string;
  items?: { name: string; url: string }[];
  breadcrumbs?: { name: string; url: string }[];
  price?: number;
  currency?: string;
  discount?: number;
  count?: number;
  barcode?: string;
  code?: string;
  rating?: number;
  rating_count?: number;
}

export function usePageSeo(info: MaybeRef<SeoInfo>) {
  const settingsStore = useSettingsStore();
  const config = useRuntimeConfig()

  const { settings } = storeToRefs(settingsStore);

  const route = useRoute();
  const url = useRequestURL();
  const { locale, defaultLocale } = useI18n();
  const localePath = useLocalePath();

  const seo = computed(() => unref(info));

  const canonicalUrl = computed(() => new URL(seo.value.canonicalPath || route.path, config.public.siteUrl).href);
  const localizedUrl = (code: string) => {
    const target = new URL(localePath(route.path, code), config.public.siteUrl);
    target.search = new URL(canonicalUrl.value).search;
    return target.href;
  };

  const ogLocale = computed(() => localeOpenGraphTag(locale.value));

  const ogType = computed(() => {
    if (seo.value.schemaType === "product") return "product";
    if (seo.value.schemaType === "article") return "article";
    return "website";
  });

  const schemaType = computed(() => {
    const map: Record<string, string> = {
      product: "Product",
      website: "WebSite",
      article: "Article",
    };

    return map[seo.value.schemaType] || seo.value.schemaType;
  });

  const favicon = computed(() => {
    if (ogType.value !== "website" && seo.value.icon) return seo.value.icon;
    return settings.value?.favicon || undefined;
  });

  const jsonLd = computed(() => {
    if (schemaType.value === "Product") {
      const product: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: seo.value.name,
        url: canonicalUrl.value,
        image: seo.value.photo ? [seo.value.photo] : undefined,
        description: seo.value.short_description || undefined,
        sku: seo.value.sku || undefined,
        mpn: seo.value.code || undefined,
        gtin13: seo.value.barcode || undefined,
        brand: seo.value.brand
          ? {
              "@type": "Brand",
              name: seo.value.brand,
              url: seo.value.brandUrl ? new URL(localePath(seo.value.brandUrl), config.public.siteUrl).href : undefined,
            }
          : undefined,
      };

      if (seo.value.price != null) {
        product.offers = {
          "@type": "Offer",
          url: canonicalUrl.value,
          priceCurrency: seo.value.currency || "EUR",
          price: seo.value.price,
          availability:
            seo.value.count != null && seo.value.count > 0
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
          itemCondition: "https://schema.org/NewCondition",
        };

        if (seo.value.count != null) {
          // @ts-expect-error Not added in this package
          product.offers.inventoryLevel = {
            "@type": "QuantitativeValue",
            value: seo.value.count,
          };
        }
      }

      if (
        seo.value.rating != null
        && seo.value.rating > 0
        && (seo.value.rating_count ?? 0) > 0
      ) {
        product.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: seo.value.rating,
          ratingCount: seo.value.rating_count,
        };
      }

      return product;
    }

    if (schemaType.value === "CollectionPage" && seo.value.brand) {
      const brandUrl = new URL(localePath(seo.value.brandUrl || route.path), config.public.siteUrl).href;
      return {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage", name: seo.value.name, url: canonicalUrl.value,
            description: seo.value.short_description || undefined,
            about: { "@id": `${brandUrl}#brand` },
            mainEntity: {
              "@type": "ItemList",
              itemListElement: (seo.value.items || []).map((item, index) => ({
                "@type": "ListItem", position: index + 1,
                name: item.name, url: new URL(localePath(item.url.startsWith("/") ? item.url : "/" + item.url), config.public.siteUrl).href,
              })),
            },
          },
          { "@type": "Brand", "@id": `${brandUrl}#brand`, name: seo.value.brand, url: brandUrl, logo: seo.value.photo || undefined },
          {
            "@type": "BreadcrumbList",
            itemListElement: (seo.value.breadcrumbs || []).map((item, index) => ({
              "@type": "ListItem", position: index + 1, name: item.name,
              item: new URL(localePath(item.url), config.public.siteUrl).href,
            })),
          },
        ],
      };
    }

    if (schemaType.value === "Article") {
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: seo.value.name,
        name: seo.value.name,
        url: canonicalUrl.value,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl.value },
        description: seo.value.short_description || undefined,
        image: seo.value.photo ? [seo.value.photo] : undefined,
        datePublished: seo.value.publishedAt || undefined,
        dateModified: seo.value.modifiedAt || undefined,
      };
    }

    return {
      "@context": "https://schema.org",
      "@type": schemaType.value,
      name: seo.value.name,
      url: canonicalUrl.value,
      description: seo.value.short_description || undefined,
    };
  });

  useSeoMeta({
    title: () => seo.value.name || "",
    description: () => seo.value.short_description || "",
    ogSiteName: () => settings.value?.site_title || url.hostname,
    ogLocale: () => ogLocale.value,

    ogTitle: () => seo.value.name || "",
    ogDescription: () => seo.value.short_description || "",
    // @ts-expect-error Not added in this package
    ogType: () => ogType.value,
    ogUrl: () => canonicalUrl.value,
    ogImage: () => seo.value.photo || undefined,
    ogImageAlt: () => seo.value.name || "",

    twitterCard: "summary_large_image",
    twitterTitle: () => seo.value.name || "",
    twitterDescription: () => seo.value.short_description || "",
    twitterImage: () => seo.value.photo || undefined,

    robots: () => seo.value.indexable === false ? "noindex,follow" : "index,follow",
  });

  useHead(() => ({
    htmlAttrs: {
      lang: locale.value,
    },
    link: [
      ...(favicon.value
        ? [
            {
              rel: "icon",
              type: "image/webp",
              href: favicon.value,
            },
          ]
        : []),
      {
        rel: "canonical",
        href: canonicalUrl.value,
      },
      ...(settings.value?.languages || []).map((lng) => ({
        rel: "alternate",
        hreflang: lng.code,
        href: localizedUrl(lng.code),
      })),
      {
        rel: "alternate",
        hreflang: "x-default",
        href: localizedUrl(defaultLocale),
      },

    ],
    script: [
      {
        key: "json-ld",
        type: "application/ld+json",
        innerHTML: JSON.stringify(jsonLd.value).replace(/</g, "\\u003c"),
      },
    ],
  }));
}
